import * as React from 'react';
import classnames from 'classnames';
import Head from 'next/head';

import {
  SITE_EMAIL_ADDRESS,
  SITE_EMAIL_LINK,
  SITE_TITLE
} from '@src/config/constants';

import styles from './faq.module.css';

export interface FAQProps {}

/**
 * @name FAQ
 * @description Frequently Asked Questions
 */
export const FAQ = (_props: FAQProps) => {
  // Styles
  const cssMain = classnames('ui-container-lg p-2x', styles.main);
  const cssHeadingFirst = classnames(styles.headingForest, styles.first);

  return (
    <>
      <Head>
        <title>{`FAQ | ${SITE_TITLE}`}</title>
      </Head>

      <main className={cssMain}>
        <h1 className="ui-page-heading pb-0">Frequently Asked Questions</h1>
        <h2 className={styles.headingRust}>Learn more about the service </h2>

        <h3 className={cssHeadingFirst}>Is it a subscription service?</h3>
        <p>
          Nope, Haldi isn’t a subscription service. You’re in control—you only
          buy what you want, when you want.
        </p>

        <h3 className={styles.headingForest}>How does Haldi work?</h3>
        <p>Here’s what you can expect:</p>
        <ul>
          <li>
            <b>Fill out our skin assessment:</b> Tell us about your goals,
            budget, lifestyle and products you currently use.
          </li>
          <li>
            <b>Keep using your faves:</b> We never want you to toss everything
            and start from scratch, so we build around what’s already on your
            bathroom shelf.
          </li>
          <li>
            <b>Get your personal routine:</b> Within 48 hours, your skin coach
            will email you an assessment of your current skincare, along with a
            personalized morning and evening routine.
          </li>
          <li>
            <b>Shop your mini store:</b> We’ll create a customized Haldi store
            and stock it with the best recommended products for you. You can
            purchase whatever you need and we’ll mail it directly to you. We
            never markup retail and shipping is free over $50.
          </li>
        </ul>

        <h3 className={styles.headingForest}>How much does Haldi cost?</h3>
        <p>
          Receiving a personal routine from your skin coach is free. We build
          you a curated store where you can preview all your recommended
          products and buy what you need. You only pay for products you buy.
          Products typically range from $20 to $100. There are never any markups
          on retail products, ever.
        </p>
        <p>
          We aim to be mindful to develop routines within the budget you
          specify, and you can always ask us for alternate suggestions.
        </p>

        <h3 className={styles.headingForest}>
          What happens when my skin changes?
        </h3>
        <p>
          We totally get that as life and the seasons change, skin does too.
          Feel free to retake the Haldi skin survey anytime and we’ll update
          your store with new recommended products. Or just reach out to{' '}
          <a href={SITE_EMAIL_LINK}>{SITE_EMAIL_ADDRESS}</a> and we’ll recommend
          updates.
        </p>
        <p>
          We’ll also add to your store every now and then, like if we find a
          product we think you’d enjoy, we’ll add it to your store.
        </p>

        <h2 className={styles.headingRust}>Our Philosophy</h2>
        <h3 className={cssHeadingFirst}>
          How do you curate your brands and products?
        </h3>
        <p>
          We are very selective in the brands and products we carry and very few
          make “the cut”. We look for next-generation brands and products from
          around the world that combine the best of science and nature, and
          deliver real results. We don’t believe any brand makes everything
          well, so we look for “hero” products, the best of the best within each
          brand and category. Every product then gets carefully vetted and
          personally tested by our team of testers.
        </p>

        <h3 className={styles.headingForest}>
          I bought a product somewhere else, is it ok to use?
        </h3>
        <p>
          If you bought a product somewhere else and want to add to your Haldi
          routine, that’s no problem. We’ll start by checking your product for
          Ingredient offenders.
        </p>
        <p>
          These are 8 ingredients which we believe should be in no one's
          skincare, ingredients doing more harm than good and where great
          alternatives are accessible to all. We’ve affectionately dubbed them
          the “trans-fats'' of skincare, as whether one believes in clean beauty
          or is still dabbling with the idea, these ingredients are best
          avoided. After all, it takes only one bad ingredient to mess up your
          routine!
        </p>
        <p>
          So regardless of where you bought the product, we’ll check your
          products for these 8 Ingredient Offenders:
        </p>
        <ul>
          <li>
            <b>Harsh surfactants:</b> Sodium Laureth Sulfate (SLES), Sodium
            Lauryl Sulfate (SLS)
          </li>
          <li>
            <b>Harsh scrub beads:</b> Walnut shell powder (selective use)
          </li>
          <li>
            <b>Drying alcohols:</b> Alcohol denat and other derivatives
            (selective use)
          </li>
          <li>
            <b>Highly comedogenic ingredients:</b> Mineral oil, Coconut oil
            (selective use)
          </li>
          <li>
            <b>Old school preservatives:</b> Parabens, Methylisothiazolinone
            (MI) /Methylchloroisothiazolinone (MCI)
          </li>
        </ul>

        <h3 className={styles.headingForest}>
          Are Haldi recommendations clean?
        </h3>
        <p>
          Yes, Haldi recommendations are clean but we believe clean is not
          enough. Just because a product is clean does not mean it will work for
          you. Something might be too rich for your acneic skin, or too harsh
          for your sensitivity. So we believe in Clean-For-You. This is how it
          works:
        </p>

        <ul>
          <li>
            <b>The Clean Standard:</b> We’ll start by ensuring all products we
            recommend do not contain ingredients which could be harmful to
            everyone. These typically fall into 2 categories
            <ul>
              <li>
                <b>Skin irritants and allergens:</b>
                <ul>
                  <li>Sodium Laureth Sulfate (SLES)</li>
                  <li>Sodium Lauryl Sulfate (SLS) </li>
                  <li>Walnut shell powder </li>
                  <li>Drying alcohols (Alcohol denat and other derivatives)</li>
                  <li>Mineral oil</li>
                  <li>Pure coconut oil</li>
                  <li>
                    Methylisothiazolinone (MI) /Methylchloroisothiazolinone
                    (MCI)
                  </li>
                  <li>BHA (Butylated Hydroxyanisole)</li>
                  <li>Hydroquinone</li>
                  <li>Methyl Cellosolve or Methoxyethanol</li>
                  <li>
                    Methylisothiazolinone (MI) / Methylchloroisothiazolinone
                    (MCI)
                  </li>
                  <li>Resorcinol</li>
                </ul>
              </li>
              <li>
                <b>Health and human safety: </b>
                <ul>
                  <li>Parabens</li>
                  <li>BHA (Butylated Hydroxyanisole) </li>
                  <li>Ethanolamines</li>
                  <li>Formaldehyde</li>
                  <li>Resorcinol</li>
                  <li>Triclosan and Triclocarban</li>
                  <li>Methyl Cellosolve or Methoxyethanol</li>
                  <li>Methylchloroisothiazolinone</li>
                  <li>Toluene</li>
                </ul>
              </li>
            </ul>
          </li>
          <li>
            <b>Clean-For-You:</b> Then we layer ingredients that you should
            avoid based on your unique skin. Pregnant or nursing? There are some
            ingredients that doctors universally recommend avoiding, so we’ll
            watch out for those. Got a gluten allergy, or rosacea? We’ll factor
            it all into our recommendations. And the list continues.
          </li>
          <li>
            <b>The Haldi exception:</b> Lastly, there’s one exception we’ve
            made, and that’s chemical sunscreen and here’s why. Too many people
            come to Haldi with no sunscreen in their routine at all, either
            because they’ve found it hard to find a sunscreen that can work
            under makeup, it leaves a white cast (especially for darker skin
            tones) or just plain sucks (as they say). For these people, we’ve
            found that chemical sunscreens, which are more cosmetically elegant
            and leave zero to minimal white cast, work well. We’ve converted so
            many people from wearing no sunscreen at all to wearing a chemical
            sunscreen, which is huge for their overall skin health!
            <br />
            So while most clean lists remove chemical sunscreen (as that is the
            ideal scenario), with all we know about how the sun affects our skin
            over time, we believe the best sunscreen is the one you wear
            everyday, whether or not it contains chemical filters.
          </li>
        </ul>

        <h2 className={styles.headingRust}>Shipping and Returns</h2>
        <h3 className={cssHeadingFirst}>How does shipping work?</h3>
        <p>
          We offer free shipping on orders over $50. We process your order the
          same day or next business day with USPS. Packages typically take 3-5
          business days to be delivered.
        </p>

        <h3 className={styles.headingForest}>Do you ship internationally?</h3>
        <p>We’re currently shipping in the U.S. only, but never say never!</p>

        <h3 className={styles.headingForest}>
          What if I don’t love my products and routine?
        </h3>
        <p>
          If you aren’t happy with an item, we can take it back for a refund.
          You can return new or gently-used products for a refund within 30 days
          of receiving your purchase.
        </p>
        <p>
          In return, we ask only for your feedback, because after all, that’s
          what makes us better at building a great skincare routine for you.
          Please email us for a return label.
        </p>

        <h2 className={styles.headingRust}>Other</h2>
        <p>
          We obsessively check our inboxes and geek out over your questions,
          skincare or otherwise. Feel free to shoot us an email at{' '}
          <a href={SITE_EMAIL_LINK}>{SITE_EMAIL_ADDRESS}</a>.
        </p>
      </main>
    </>
  );
};

export default FAQ;
